import { useMemo } from 'react';

import { StarIcon } from '@hugeicons-pro/core-solid-rounded';
import { File01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { FieldTypes } from 'constants/field-types-enum';
import { knownFormats } from 'constants/forms';
import { saveAs } from 'file-saver';
import {
  downloadAllAsZip as downloadAllAsZipUtil,
  getFileExtensionFromUrl,
  getFilenameFromUrl,
} from 'helpers/document-download-helpers';
import moment from 'moment';
import Moment from 'react-moment';

import { Button, MultipleChoice } from 'components/FormComponents';

/**
 * Options for the formatted answer renderer (download handlers and zip loading state).
 * @param { (url?: string) => void } [onDownload] - Called to download a single file (defaults to saveAs)
 * @param { (urls: string[], zipName: string) => void } [onDownloadAllAsZip] - Called to download multiple as zip
 * @param { boolean } [zipLoading] - Whether a zip is currently being prepared
 */
/**
 * Shared hook to render a form field answer with consistent UI (scale, stars, uploads, etc.).
 * Used by QuestionResponse (single response) and QuestionResponses (aggregated responses).
 *
 * @param { object } field - Form field config (type, options, text, etc.)
 * @param { any } answer - The answer value(s)
 * @param { object } [options] - Optional { onDownload, onDownloadAllAsZip, zipLoading }
 * @returns { React.ReactNode | undefined } Formatted answer JSX or undefined
 */
export function useFormattedAnswer(field, answer, options = {}) {
  const {
    onDownload: optionsDownload,
    onDownloadAllAsZip: optionsDownloadAllAsZip,
    zipLoading: optionsZipLoading = false,
  } = options;

  const download = useMemo(() => {
    if (optionsDownload) return optionsDownload;
    return url => {
      const targetUrl = url ?? answer;
      if (!targetUrl) return;
      saveAs(targetUrl, getFilenameFromUrl(targetUrl, 0));
    };
  }, [optionsDownload, answer]);

  const downloadAllAsZip = useMemo(() => {
    if (optionsDownloadAllAsZip) return optionsDownloadAllAsZip;
    return (urls, zipName = 'documents.zip') =>
      downloadAllAsZipUtil(urls, zipName, { onStart: () => {}, onEnd: () => {} });
  }, [optionsDownloadAllAsZip]);

  const zipLoading = optionsZipLoading;

  return useMemo(() => {
    if (answer == null || answer === '') return undefined;

    switch (field?.type) {
      case FieldTypes.OpinionScale: {
        const customSteps = field.options?.use_custom_steps;
        const stepsEnd = customSteps ? field.options.steps_end : 10;
        return (
          <div className="flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-primary-5 h-2 rounded-full transition-all"
                  style={{
                    width: `${Math.min(100, (answer / stepsEnd) * 100)}%`,
                  }}
                />
              </div>
            </div>
            <span className="text-lg font-semibold text-neutral-7 whitespace-nowrap">
              {answer}/{stepsEnd}
            </span>
          </div>
        );
      }
      case FieldTypes.StarRating: {
        const customStars = field.options?.use_custom_stars;
        const starsEnd = customStars ? field.options.stars : 5;
        return (
          <div className="flex items-center gap-3">
            <div className="flex gap-2">
              {[...Array(starsEnd)].map((_, i) => (
                <HugeiconsIcon
                  key={i}
                  icon={StarIcon}
                  size={20}
                  className={cn({ 'text-amber-400': i < answer, 'text-slate-300': i >= answer })}
                />
              ))}
            </div>
            <span className="text-lg font-medium text-neutral-6">
              {answer} of {starsEnd} stars
            </span>
          </div>
        );
      }
      case FieldTypes.MultipleChoice: {
        const answers = Array.isArray(answer) ? answer : answer.split(' / ');
        const optionsList = (field.options?.options || []).map(opt => ({
          value: opt?.value ?? opt,
          name: opt?.name ?? opt?.value ?? opt,
        }));
        const optionValues = new Set(optionsList.map(opt => String(opt.value ?? '').toLowerCase()));
        const otherAnswers = field.options?.other_option
          ? (answers || []).filter(ans => !optionValues.has(String(ans ?? '').toLowerCase()))
          : [];
        return (
          <MultipleChoice
            value={field.options?.multiple_selection ? answers : answers[0]}
            onChange={() => {}}
            disabled
            smallMargin
            options={{
              multipleSelection: field.options?.multiple_selection,
              otherOption: field.options?.other_option,
              options: optionsList,
            }}
          />
        );
      }
      case FieldTypes.Date:
        return (
          <span>
            {(() => {
              const parsedDate = moment(answer, knownFormats, true);
              return parsedDate?.isValid() ? (
                <Moment format="MMMM Do, YYYY">{parsedDate.toDate()}</Moment>
              ) : (
                <span>{answer}</span>
              );
            })()}
          </span>
        );
      case FieldTypes.Upload: {
        const fileName = getFilenameFromUrl(answer);
        const fileExt = getFileExtensionFromUrl(answer);
        return (
          <div className="bg-slate-50 rounded-2xl p-4 border border-neutral-4">
            <div className="flex items-start gap-3">
              <HugeiconsIcon icon={File01Icon} size={20} className="text-neutral-6" />
              <div className="flex-1 min-w-0">
                <p className="text-xl font-medium text-neutral-7 truncate">
                  {fileName ? fileName : 'Downloaded the file to view'}
                </p>
                {fileExt && (
                  <p className="text-lg font-regular text-neutral-5 mt-1 uppercase tracking-wide">
                    {fileExt}
                  </p>
                )}
              </div>
              <Button
                icon="download"
                small
                light
                type="secondary-reverse"
                onClick={() => download(answer)}>
                Download
              </Button>
            </div>
          </div>
        );
      }
      case FieldTypes.MultiUpload: {
        const urls = Array.isArray(answer) ? answer : [answer];
        const hasMultiple = (urls || []).length > 1;
        const firstUrl = urls?.[0];
        const multiFileName = getFilenameFromUrl(firstUrl);
        const multiFileExt = getFileExtensionFromUrl(firstUrl);
        const zipName =
          (field.text || '')
            .replace(/<[^>]*>/g, '')
            .trim()
            .replace(/[^\w\s-]/g, '')
            .replace(/\s+/g, '-')
            .slice(0, 40) || 'documents';
        return (
          <div className="bg-slate-50 rounded-2xl p-4 border border-neutral-4">
            <div className="flex items-start gap-3">
              <HugeiconsIcon icon={File01Icon} size={20} className="text-neutral-6" />
              <div className="flex-1 min-w-0">
                <p className="text-xl font-medium text-neutral-7 truncate">
                  {multiFileName ? multiFileName : 'Downloaded the file to view'}
                </p>
                {multiFileExt && (
                  <p className="text-lg font-regular text-neutral-5 mt-1 uppercase tracking-wide">
                    {multiFileExt}
                  </p>
                )}
              </div>
              {hasMultiple ? (
                <Button
                  onClick={() => downloadAllAsZip(urls, `${zipName}.zip`)}
                  icon="download"
                  small
                  light
                  type="secondary-reverse"
                  disabled={zipLoading}>
                  {zipLoading ? 'Preparing…' : 'Download all as ZIP'}
                </Button>
              ) : (
                (urls || [])?.map((url, index) => (
                  <Button
                    key={index}
                    onClick={() => download(url)}
                    icon="download"
                    small
                    light
                    type="secondary-reverse">
                    View document
                  </Button>
                ))
              )}
            </div>
          </div>
        );
      }
      case FieldTypes.Signature:
        return (
          <img
            className={cn('pointer-events-none cursor-none h-[70px] w-auto')}
            src={answer}
            alt="Signature"
          />
        );
      default:
        return <span className="text-xl font-regular text-slate-600">{answer}</span>;
    }
  }, [answer, field, zipLoading, download, downloadAllAsZip]);
}

/**
 * Component wrapper for useFormattedAnswer so it can be used in loops (e.g. multiple answers per question).
 * Use this when rendering many answers; use useFormattedAnswer directly for a single answer.
 */
export function FormattedAnswer({ field, answer, onDownload, onDownloadAllAsZip, zipLoading }) {
  const content = useFormattedAnswer(field, answer, {
    onDownload,
    onDownloadAllAsZip,
    zipLoading,
  });
  return content ?? null;
}
